import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Charts from './components/Charts';
import Topbar from './components/TopBar';
import AppRoutes from './routes/AppRoutes';

import {
  isTokenExpired,
  removeToken,
  getToken,
  decodeToken,
} from './utils/TokenUtils';

import { updateFavicon } from './utlis';
import { API_URL, REACT_APP_LAYOUT } from './constants/api';
import socket from './utils/socket';

import useFCM from './firebase/useFCM';
import { requestFCMToken } from './firebase/firebase';
import { ToastContainer } from 'react-toastify';
import NavigationPage from './components/NavigationPage';
import NavigationTabs from './components/NavigationTabs';
import { Tab } from 'bootstrap';

// ================= CONFIG =================

const sharedRoutes = [
  '/portal-promo',
  '/pos/pending/:route',
  'orders-feedback',
  '/charts/:route',
  '/qr-code',
  '/ai-translato',
];

const ROUTES = {
  hideNavbar: [
    '/',
    '/login/:loginRoute',
    '/forgot-password/:forgotRoute',
    '/reset-password/:token',
    '/verify-email/:token',
    '/register-2fa',
    '/check-2fa',
    '/pos-invoice/:route/:id',
    '/pos-invoice/:route',
    '/sales-invoice/:route/:id',
    '/student-invoice/:route/:id',
    '/student-invoice/:route/:fee_id/:id',
    '/portal/:route',
    '/invoice/:route/:id',
    '/tracking/:route',
    '/qrcode/:route/:id',
    '/documentation/:_id/:type',
    '/sys-features/:_id/:type',
    '/login-documentation/:email',
    '/registration/international-ngo-form',
    '/multiRowPrint/:route/:id',
    '/multiRowPrintinv/:route/:id',
    '/salesPrint/:route/:id',
    '/idcard/id/:id',
    '/pos/jaziira/:route/:id',
    '/api-documentation/:link_id',
    '/print-activity',
    '/print',
    '/Certificate/:certificate_id',
    '/passcard/:route/:id/:exam_id',
    '/passcard2/:route/:id/:exam_id',
    '/class-passcard/:route/:class_id',
    '/class-passcard2/:route/:class_id',
    '/pos/pending-orders',

    '/php-my-admin',
    '/questions',
    '/exam-admin',
    '/student-portal',
    '/custom-invoice/:route/:ref_no',
    '/student-letter/:route/:id',
    '/report/ktc-sql',
    ...sharedRoutes,
  ],

  publicPaths: [
    '/login/',
    '/portal/',
    '/forgot-',
    '/reset-',
    '/verify-',
    '-2fa',
    '/web',
    '/registration-letter',
    '/elearning',
    '/Certificate',
    'live-exam-students',
    'kaafipay/',
    'idcard/',
    'multiRowPrint/',
    'multiRowPrintinv/',
    'salesPrint/',
    '/privacy-policy',
    ...sharedRoutes,
  ],

  hideNavbarAndPublicCombined: sharedRoutes,

  showCharts: ['/dashboard', '/charts'],
};

// ================= HELPERS =================

const matchRoute = (path, pattern) => {
  const regex = new RegExp(`^${pattern.replace(/:[^/]+/g, '([^/]+)')}$`);
  return regex.test(path);
};

const matchAny = (path, routes) =>
  routes.some((route) => matchRoute(path, route));

const includesAny = (path, patterns) => patterns.some((p) => path.includes(p));

// ================= MAIN APP =================

const App = () => {
  useFCM();

  useEffect(() => {
    navigator.serviceWorker
      .register('/firebase-messaging-sw.js', { type: 'classic' })
      .then((reg) => {
        // console.log('SW registered:', reg)
      })
      .catch((err) => {
        console.log('SW error:', err);
      });

    const initFCM = async () => {
      if (Notification.permission !== 'granted') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;
      }
      const token = await requestFCMToken();
      // console.log('FCM Token:', token);
    };

    initFCM();
  }, []);

  return (
    <Router>
      <ToastContainer />
      <AppContent />
    </Router>
  );
};

// ================= APP CONTENT =================

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const isPublic = includesAny(path, ROUTES.publicPaths);
  console.log('path:', path, 'isPublic:', isPublic);
  const token = getToken();

  // ================= AUTH =================
  useEffect(() => {
    const loginPage = decodeToken(token, 'loginPage');
    const isEnabled2FA = decodeToken(token, 'isEnabled2FA');
    const isVerified2FA = decodeToken(token, 'is2FaVerified');
    const logoUrl = decodeToken(token, 'logo');

    if (!token && !isPublic) {
      return navigate('/login/user-login');
    }

    if (token && isTokenExpired(token)) {
      removeToken();
      return navigate(`/login/${loginPage}`);
    }

    if (isEnabled2FA && !isVerified2FA) {
      return navigate('/check-2fa');
    }

    if (logoUrl) {
      updateFavicon(`${API_URL}/${logoUrl}`);
    }

    if (token && socket) {
      socket.on('receive-notification', (data) => {
        // console.log('🔔 Notification:', data);
      });

      return () => socket.off('receive-notification');
    }
  }, [path, navigate, token, isPublic]);

  // ================= UI LOGIC =================

  const shouldHideNavbar = matchAny(path, ROUTES.hideNavbar) || isPublic;
  const shouldShowCharts = ROUTES.showCharts.includes(path);
  const layout = localStorage.getItem('app_layout') || REACT_APP_LAYOUT;

  // ================= RENDER =================
  console.log('layout:', layout);
  if (layout === 'V2') {
    if (shouldHideNavbar) return <AppRoutes />;

    return (
      <div className="d-flex">
        <Sidebar />

        <div className="flex-grow-1 d-flex flex-column">
          <Topbar />

          <div
            className="flex-grow-1 overflow-auto"
            style={{ marginTop: '130px', marginLeft: '280px' }}
          >
            {shouldShowCharts && <Charts />}
            <AppRoutes />

            <Footer />
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'V3') {
    if (shouldHideNavbar) return <AppRoutes />;

    return (
      <div className="d-flex">
        {/* Hide Sidebar in V3 */}

        <div className="flex-grow-1 d-flex flex-column">
          <Topbar />

          <div
            className="flex-grow-1 overflow-auto"
            style={{ marginTop: '130px' }}
          >
            <NavigationTabs />

            <Footer />
          </div>
        </div>
      </div>
    );
  }

  // ===== V1 Layout =====
  return (
    <>
      {!shouldHideNavbar && <Navbar />}
      {shouldShowCharts && <Charts />}
      <AppRoutes />
      {!shouldHideNavbar && <Footer />}
    </>
  );
};

export default App;
